import type { Security, SecurityMetadata } from '../types/Security';

export const SECURITY_IDENTIFIER_METADATA_KEYS = ['FIGI', 'CUSIP', 'ISIN', 'SEDOL'] as const;

export type SecurityIdentifierMetadataKey = (typeof SECURITY_IDENTIFIER_METADATA_KEYS)[number];

function isSecurityMetadata(value: unknown): value is SecurityMetadata {
  return value != null && typeof value === 'object' && !Array.isArray(value);
}

export function getSecurityMetadata(metadata: Security['Metadata']): SecurityMetadata {
  if (metadata == null) {
    return {};
  }
  if (isSecurityMetadata(metadata)) {
    return metadata;
  }

  const trimmed = metadata.trim();
  if (!trimmed) {
    return {};
  }
  try {
    const parsed: unknown = JSON.parse(trimmed);
    return isSecurityMetadata(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

const parsedMetadataBySecurity = new WeakMap<Security, SecurityMetadata>();

export function getCachedSecurityMetadata(security: Security): SecurityMetadata {
  const cached = parsedMetadataBySecurity.get(security);
  if (cached) {
    return cached;
  }

  const metadata = getSecurityMetadata(security.Metadata);
  parsedMetadataBySecurity.set(security, metadata);
  return metadata;
}

export function getSecurityIdentifierMetadataKeys(includeSedol: boolean): readonly SecurityIdentifierMetadataKey[] {
  return includeSedol
    ? SECURITY_IDENTIFIER_METADATA_KEYS
    : SECURITY_IDENTIFIER_METADATA_KEYS.filter(key => key !== 'SEDOL');
}

export function getSecurityIdentifierValues(security: Security, includeSedol = false): string[] {
  const metadata = getCachedSecurityMetadata(security);

  return getSecurityIdentifierMetadataKeys(includeSedol).flatMap(key => {
    const value = metadata[key];
    if (typeof value !== 'string') {
      return [];
    }

    const trimmed = value.trim();
    return trimmed === '' ? [] : [trimmed];
  });
}

export function getSecurityIdentifierSearchText(security: Security | undefined, includeSedol = false): string {
  return security ? getSecurityIdentifierValues(security, includeSedol).join(' ') : '';
}

export function omitSedolFromSecurityForDisplay(security: Security, includeSedol: boolean): Security {
  if (includeSedol) {
    return security;
  }

  const metadata = getCachedSecurityMetadata(security);
  if (metadata.SEDOL == null) {
    return security;
  }

  const { SEDOL: _sedol, ...metadataWithoutSedol } = metadata;
  if (typeof security.Metadata === 'string') {
    return { ...security, Metadata: JSON.stringify(metadataWithoutSedol) };
  }

  return { ...security, Metadata: metadataWithoutSedol };
}
